import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Input, Loader, Select } from './shared';

const ProjectBackup = ({ service, isOnline }) => {
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [vaultStatus, setVaultStatus] = useState(null);

  // Form state for creating new projects
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    backup_type: 'full_project',
    template: 'custom',
    enabled: true,
    // Component selections
    includeRepositories: false,
    includeDatabases: false,
    includeFilesystem: false,
    // Repository configuration
    repositories: [
      {
        name: '',
        repository_url: '',
        branch: 'main',
        include_issues: true,
        include_releases: true,
        priority: 1
      }
    ],
    // Database configuration
    databases: [
      {
        name: '',
        database_name: '',
        include_schema: true,
        include_data: true,
        priority: 1
      }
    ],
    // Filesystem configuration
    filesystem_paths: [''],
    schedule: {
      frequency: 'daily',
      time_of_day: '02:00'
    },
    storage_location: 'local',
    encryption_enabled: true
  });

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const fetchData = useCallback(async () => {
    if (!isOnline) return;

    setLoading(true);
    try {
      // Fetch projects, jobs, stats, vault status, and templates in parallel
      const [projectsRes, jobsRes, statsRes, vaultRes, templatesRes] = await Promise.all([
        fetch(`${service.url}/api/v1/projects`, {
          headers: { 'Authorization': `Bearer ${service.apiKey}` }
        }),
        fetch(`${service.url}/api/v1/jobs?limit=20`, {
          headers: { 'Authorization': `Bearer ${service.apiKey}` }
        }),
        fetch(`${service.url}/api/v1/stats`, {
          headers: { 'Authorization': `Bearer ${service.apiKey}` }
        }),
        fetch(`${service.url}/api/v1/config/vault-status`, {
          headers: { 'Authorization': `Bearer ${service.apiKey}` }
        }),
        fetch(`${service.url}/api/v1/templates`, {
          headers: { 'Authorization': `Bearer ${service.apiKey}` }
        })
      ]);

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(projectsData);
      }

      if (jobsRes.ok) {
        const jobsData = await jobsRes.json();
        setJobs(jobsData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (vaultRes.ok) {
        const vaultData = await vaultRes.json();
        setVaultStatus(vaultData);
      }

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData.templates);
      }

    } catch (error) {
      console.error('Failed to fetch backup data:', error);
    } finally {
      setLoading(false);
    }
  }, [service, isOnline]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  const createProject = async () => {
    try {
      // Build the project configuration based on selected components
      const projectConfig = {
        name: newProject.name,
        description: newProject.description,
        backup_type: newProject.backup_type,
        template: newProject.template,
        enabled: newProject.enabled,
        schedule: newProject.schedule,
        storage_location: newProject.storage_location,
        encryption_enabled: newProject.encryption_enabled
      };

      // Add GitHub config if repositories are included
      if (newProject.includeRepositories && newProject.repositories.some(r => r.repository_url)) {
        projectConfig.github_config = {
          repositories: newProject.repositories.filter(r => r.repository_url).map(r => ({
            name: r.name || r.repository_url.split('/').pop().replace('.git', ''),
            repository_url: r.repository_url,
            branch: r.branch,
            include_issues: r.include_issues,
            include_releases: r.include_releases,
            priority: r.priority
          })),
          parallel_backups: 2
        };
      }

      // Add PostgreSQL config if databases are included
      if (newProject.includeDatabases && newProject.databases.some(d => d.database_name)) {
        projectConfig.postgres_config = {
          databases: newProject.databases.filter(d => d.database_name).map(d => ({
            name: d.name || d.database_name,
            database_name: d.database_name,
            include_schema: d.include_schema,
            include_data: d.include_data,
            priority: d.priority
          })),
          parallel_backups: 2
        };
      }

      // Add filesystem config if paths are included
      if (newProject.includeFilesystem && newProject.filesystem_paths.some(p => p)) {
        projectConfig.filesystem_config = {
          source_paths: newProject.filesystem_paths.filter(p => p),
          exclude_patterns: [],
          follow_symlinks: false,
          compress: true
        };
      }

      const response = await fetch(`${service.url}/api/v1/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${service.apiKey}`
        },
        body: JSON.stringify(projectConfig)
      });

      if (response.ok) {
        setShowCreateForm(false);
        resetNewProjectForm();
        fetchData();
      } else {
        const errorData = await response.json();
        alert(`Failed to create project: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project');
    }
  };

  const resetNewProjectForm = () => {
    setNewProject({
      name: '',
      description: '',
      backup_type: 'full_project',
      template: 'custom',
      enabled: true,
      includeRepositories: false,
      includeDatabases: false,
      includeFilesystem: false,
      repositories: [
        {
          name: '',
          repository_url: '',
          branch: 'main',
          include_issues: true,
          include_releases: true,
          priority: 1
        }
      ],
      databases: [
        {
          name: '',
          database_name: '',
          include_schema: true,
          include_data: true,
          priority: 1
        }
      ],
      filesystem_paths: [''],
      schedule: {
        frequency: 'daily',
        time_of_day: '02:00'
      },
      storage_location: 'local',
      encryption_enabled: true
    });
    setSelectedTemplate(null);
  };

  const addRepository = () => {
    setNewProject({
      ...newProject,
      repositories: [...newProject.repositories, {
        name: '',
        repository_url: '',
        branch: 'main',
        include_issues: true,
        include_releases: true,
        priority: 1
      }]
    });
  };

  const removeRepository = (index) => {
    const repositories = newProject.repositories.filter((_, i) => i !== index);
    setNewProject({ ...newProject, repositories });
  };

  const updateRepository = (index, field, value) => {
    const repositories = [...newProject.repositories];
    repositories[index] = { ...repositories[index], [field]: value };
    setNewProject({ ...newProject, repositories });
  };

  const addDatabase = () => {
    setNewProject({
      ...newProject,
      databases: [...newProject.databases, {
        name: '',
        database_name: '',
        include_schema: true,
        include_data: true,
        priority: 1
      }]
    });
  };

  const removeDatabase = (index) => {
    const databases = newProject.databases.filter((_, i) => i !== index);
    setNewProject({ ...newProject, databases });
  };

  const updateDatabase = (index, field, value) => {
    const databases = [...newProject.databases];
    databases[index] = { ...databases[index], [field]: value };
    setNewProject({ ...newProject, databases });
  };

  const addFilesystemPath = () => {
    setNewProject({
      ...newProject,
      filesystem_paths: [...newProject.filesystem_paths, '']
    });
  };

  const removeFilesystemPath = (index) => {
    const paths = newProject.filesystem_paths.filter((_, i) => i !== index);
    setNewProject({ ...newProject, filesystem_paths: paths });
  };

  const updateFilesystemPath = (index, value) => {
    const paths = [...newProject.filesystem_paths];
    paths[index] = value;
    setNewProject({ ...newProject, filesystem_paths: paths });
  };

  const applyTemplate = (templateName) => {
    const template = templates[templateName];
    if (!template) return;

    setSelectedTemplate(template);
    
    const updatedProject = {
      ...newProject,
      backup_type: template.backup_type,
      template: templateName,
      includeRepositories: !!template.example_config.github_config,
      includeDatabases: !!template.example_config.postgres_config,
      includeFilesystem: !!template.example_config.filesystem_config
    };

    // Apply repository configuration from template
    if (template.example_config.github_config) {
      updatedProject.repositories = template.example_config.github_config.repositories.map(repo => ({
        name: repo.name,
        repository_url: repo.repository_url,
        branch: repo.branch,
        include_issues: repo.include_issues || true,
        include_releases: repo.include_releases || true,
        priority: 1
      }));
    }

    // Apply database configuration from template
    if (template.example_config.postgres_config) {
      updatedProject.databases = template.example_config.postgres_config.databases.map(db => ({
        name: db.name,
        database_name: db.database_name,
        include_schema: true,
        include_data: true,
        priority: 1
      }));
    }

    setNewProject(updatedProject);
  };

  const triggerBackup = async (projectId) => {
    try {
      const response = await fetch(`${service.url}/api/v1/projects/${projectId}/backup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${service.apiKey}` }
      });

      if (response.ok) {
        fetchData(); // Refresh data to show new job
      } else {
        alert('Failed to trigger backup');
      }
    } catch (error) {
      console.error('Failed to trigger backup:', error);
      alert('Failed to trigger backup');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      const response = await fetch(`${service.url}/api/v1/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${service.apiKey}` }
      });

      if (response.ok) {
        fetchData();
      } else {
        alert('Failed to delete project');
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  if (!isOnline) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">
          <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <h3 className="text-lg font-semibold">Project Backup Service Offline</h3>
          <p className="text-gray-600 mt-2">The backup service is currently unavailable. Please check the service status.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with tabs */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Project Backup Manager</h2>
          {stats && (
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>{stats.total_projects} projects</span>
              <span>{stats.total_backups} backups</span>
              <span>{stats.success_rate_percentage.toFixed(1)}% success rate</span>
            </div>
          )}
        </div>

        {/* Vault Status */}
        {vaultStatus && (
          <div className={`mb-4 p-3 rounded-lg ${vaultStatus.vault_connected ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center">
              <span className={`w-2 h-2 rounded-full mr-2 ${vaultStatus.vault_connected ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span className="text-sm font-medium">
                Secrets Vault: {vaultStatus.vault_connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            {!vaultStatus.vault_connected && vaultStatus.guidance && vaultStatus.guidance.length > 0 && (
              <div className="mt-2 text-sm text-red-700">
                <p className="font-medium">Setup required:</p>
                <ul className="list-disc list-inside mt-1">
                  {vaultStatus.guidance.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('projects')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'projects'
                ? 'border-[#FFC65D] text-[#FFC65D]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Projects ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'jobs'
                ? 'border-[#FFC65D] text-[#FFC65D]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Recent Jobs ({jobs.length})
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stats'
                ? 'border-[#FFC65D] text-[#FFC65D]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Statistics
          </button>
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'projects' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">Backup Projects</h3>
              <Button
                onClick={() => setShowCreateForm(true)}
                variant="primary"
              >
                Create Project
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader size="md" text="Loading projects..." />
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No backup projects</h3>
                <p className="text-gray-600 mb-4">Create your first backup project to get started.</p>
                <Button
                  onClick={() => setShowCreateForm(true)}
                  variant="primary"
                >
                  Create Project
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {projects.map((project) => (
                  <div key={project.id} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{project.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={project.enabled ? 'success' : 'info'}>
                          {project.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Badge variant="info">
                          {project.backup_type.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 space-y-2">
                      {project.component_summary && (
                        <div className="flex justify-between">
                          <span>Components:</span>
                          <span className="font-medium">
                            {[
                              project.component_summary.total_repositories > 0 && `${project.component_summary.total_repositories} repos`,
                              project.component_summary.total_databases > 0 && `${project.component_summary.total_databases} DBs`,
                              project.component_summary.total_file_systems > 0 && `${project.component_summary.total_file_systems} paths`
                            ].filter(Boolean).join(', ') || 'None'}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Schedule:</span>
                        <span className="font-medium">{project.schedule.frequency} at {project.schedule.time_of_day}</span>
                      </div>
                      {project.component_summary?.estimated_backup_time_minutes && (
                        <div className="flex justify-between">
                          <span>Est. Duration:</span>
                          <span className="font-medium">{Math.round(project.component_summary.estimated_backup_time_minutes)} min</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Encryption:</span>
                        <span className="font-medium">{project.encryption_enabled ? 'Enabled' : 'Disabled'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Storage:</span>
                        <span className="font-medium">{project.storage_location}</span>
                      </div>
                    </div>

                    <div className="mt-4 flex space-x-2">
                      <Button
                        onClick={() => triggerBackup(project.id)}
                        variant="secondary"
                        style={{ flex: 1 }}
                      >
                        Backup Now
                      </Button>
                      <Button
                        onClick={() => deleteProject(project.id)}
                        variant="danger"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Recent Backup Jobs</h3>
            
            {loading ? (
              <div className="text-center py-8">
                <Loader size="md" text="Loading jobs..." />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No backup jobs found.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Project</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Size</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Started</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {job.project_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.backup_type.replace('_', ' ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={
                            job.status === 'completed' ? 'success' :
                            job.status === 'running' ? 'info' :
                            job.status === 'failed' ? 'danger' :
                            'warning'
                          }>
                            {job.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.duration_seconds ? formatDuration(job.duration_seconds) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.backup_size_bytes ? formatFileSize(job.backup_size_bytes) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && stats && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Backup Statistics</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Projects</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_projects}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Success Rate</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.success_rate_percentage.toFixed(1)}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Storage</p>
                    <p className="text-2xl font-bold text-gray-900">{formatFileSize(stats.total_storage_used_bytes)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Avg Duration</p>
                    <p className="text-2xl font-bold text-gray-900">{formatDuration(stats.average_backup_duration_seconds)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Backup Details</h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Backups:</span>
                    <span className="font-medium">{stats.total_backups}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Successful:</span>
                    <span className="font-medium text-green-600">{stats.successful_backups}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-medium text-red-600">{stats.failed_backups}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Average Size:</span>
                    <span className="font-medium">{formatFileSize(stats.average_backup_size_bytes)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Largest Backup:</span>
                    <span className="font-medium">{formatFileSize(stats.largest_backup_bytes)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">System Health</h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Active Projects:</span>
                    <span className="font-medium">{stats.active_projects}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Vault Connection:</span>
                    <span className={`font-medium ${stats.vault_connection_status ? 'text-green-600' : 'text-red-600'}`}>
                      {stats.vault_connection_status ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Last Backup:</span>
                    <span className="font-medium">
                      {stats.last_backup_time ? new Date(stats.last_backup_time).toLocaleString() : 'Never'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Next Scheduled:</span>
                    <span className="font-medium">
                      {stats.next_scheduled_backup ? new Date(stats.next_scheduled_backup).toLocaleString() : 'None'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create Multi-Component Backup Project</h3>
            
            <div className="space-y-6">
              {/* Basic Project Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    type="text"
                    label="Project Name"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    placeholder="My Project"
                    fullWidth
                  />
                </div>

                <div>
                  <Select
                    label="Schedule Frequency"
                    value={newProject.schedule.frequency}
                    onChange={(e) => setNewProject({
                      ...newProject,
                      schedule: { ...newProject.schedule, frequency: e.target.value }
                    })}
                    options={[
                      { value: 'manual', label: 'Manual Only' },
                      { value: 'hourly', label: 'Hourly' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                      { value: 'monthly', label: 'Monthly' }
                    ]}
                    fullWidth
                  />
                </div>
              </div>

              <div>
                <Input
                  type="textarea"
                  label="Description"
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={2}
                  placeholder="Project description"
                  fullWidth
                />
              </div>

              {/* Template Selection */}
              {Object.keys(templates).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quick Start Templates</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {Object.entries(templates).map(([key, template]) => (
                      <button
                        key={key}
                        onClick={() => applyTemplate(key)}
                        className={`p-3 border rounded-lg text-left transition-colors ${
                          selectedTemplate?.name === template.name
                            ? 'border-[#FFC65D] bg-[#FFC65D] bg-opacity-10'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-gray-500 mt-1">{template.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Component Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Backup Components</label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newProject.includeRepositories}
                      onChange={(e) => setNewProject({ ...newProject, includeRepositories: e.target.checked })}
                      className="rounded border-gray-300 text-[#FFC65D] shadow-sm focus:border-[#FFC65D] focus:ring focus:ring-[#FFC65D] focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">GitHub Repositories</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newProject.includeDatabases}
                      onChange={(e) => setNewProject({ ...newProject, includeDatabases: e.target.checked })}
                      className="rounded border-gray-300 text-[#FFC65D] shadow-sm focus:border-[#FFC65D] focus:ring focus:ring-[#FFC65D] focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">PostgreSQL Databases</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newProject.includeFilesystem}
                      onChange={(e) => setNewProject({ ...newProject, includeFilesystem: e.target.checked })}
                      className="rounded border-gray-300 text-[#FFC65D] shadow-sm focus:border-[#FFC65D] focus:ring focus:ring-[#FFC65D] focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">File System Paths</span>
                  </label>
                </div>
              </div>

              {/* Repository Configuration */}
              {newProject.includeRepositories && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-900">GitHub Repositories</h4>
                    <button
                      onClick={addRepository}
                      className="text-sm text-[#FFC65D] hover:text-[#FFC65D] hover:opacity-80"
                    >
                      + Add Repository
                    </button>
                  </div>
                  <div className="space-y-3">
                    {newProject.repositories.map((repo, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded">
                        <Input
                          type="text"
                          placeholder="Repository Name"
                          value={repo.name}
                          onChange={(e) => updateRepository(index, 'name', e.target.value)}
                          fullWidth
                        />
                        <Input
                          type="text"
                          placeholder="https://github.com/user/repo"
                          value={repo.repository_url}
                          onChange={(e) => updateRepository(index, 'repository_url', e.target.value)}
                          fullWidth
                        />
                        <div className="flex items-center space-x-2">
                          <Input
                            type="text"
                            placeholder="Branch"
                            value={repo.branch}
                            onChange={(e) => updateRepository(index, 'branch', e.target.value)}
                            fullWidth
                          />
                          {newProject.repositories.length > 1 && (
                            <button
                              onClick={() => removeRepository(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Database Configuration */}
              {newProject.includeDatabases && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-900">PostgreSQL Databases</h4>
                    <button
                      onClick={addDatabase}
                      className="text-sm text-[#FFC65D] hover:text-[#FFC65D] hover:opacity-80"
                    >
                      + Add Database
                    </button>
                  </div>
                  <div className="space-y-3">
                    {newProject.databases.map((db, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded">
                        <Input
                          type="text"
                          placeholder="Database Identifier"
                          value={db.name}
                          onChange={(e) => updateDatabase(index, 'name', e.target.value)}
                          fullWidth
                        />
                        <Input
                          type="text"
                          placeholder="Database Name"
                          value={db.database_name}
                          onChange={(e) => updateDatabase(index, 'database_name', e.target.value)}
                          fullWidth
                        />
                        <div className="flex items-center space-x-2">
                          <div className="flex space-x-3">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={db.include_schema}
                                onChange={(e) => updateDatabase(index, 'include_schema', e.target.checked)}
                                className="rounded border-gray-300 text-[#FFC65D] shadow-sm"
                              />
                              <span className="ml-1 text-xs">Schema</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={db.include_data}
                                onChange={(e) => updateDatabase(index, 'include_data', e.target.checked)}
                                className="rounded border-gray-300 text-[#FFC65D] shadow-sm"
                              />
                              <span className="ml-1 text-xs">Data</span>
                            </label>
                          </div>
                          {newProject.databases.length > 1 && (
                            <button
                              onClick={() => removeDatabase(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Filesystem Configuration */}
              {newProject.includeFilesystem && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-900">File System Paths</h4>
                    <button
                      onClick={addFilesystemPath}
                      className="text-sm text-[#FFC65D] hover:text-[#FFC65D] hover:opacity-80"
                    >
                      + Add Path
                    </button>
                  </div>
                  <div className="space-y-2">
                    {newProject.filesystem_paths.map((path, index) => (
                      <div key={index} className="flex space-x-2">
                        <Input
                          type="text"
                          placeholder="/path/to/directory"
                          value={path}
                          onChange={(e) => updateFilesystemPath(index, e.target.value)}
                          fullWidth
                        />
                        {newProject.filesystem_paths.length > 1 && (
                          <button
                            onClick={() => removeFilesystemPath(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Project Summary</h4>
                <div className="text-sm text-gray-700">
                  <div>Components: {[
                    newProject.includeRepositories && `${newProject.repositories.filter(r => r.repository_url).length} repositories`,
                    newProject.includeDatabases && `${newProject.databases.filter(d => d.database_name).length} databases`,
                    newProject.includeFilesystem && `${newProject.filesystem_paths.filter(p => p).length} filesystem paths`
                  ].filter(Boolean).join(', ') || 'None selected'}</div>
                  <div>Schedule: {newProject.schedule.frequency}</div>
                  <div>Storage: {newProject.storage_location} (encrypted: {newProject.encryption_enabled ? 'yes' : 'no'})</div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                onClick={() => {
                  setShowCreateForm(false);
                  resetNewProjectForm();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={createProject}
                disabled={!newProject.name || (!newProject.includeRepositories && !newProject.includeDatabases && !newProject.includeFilesystem)}
                variant="primary"
              >
                Create Project
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectBackup;